import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { isMobile } from 'react-device-detect';
import cloneDeep from 'lodash-es/cloneDeep';
import autoBind from 'react-autobind';
import classNames from 'classnames/bind';

import {
  defaultEquityChartBarData, defaultEquityChartLayoutData,
} from '../../../../../data/ukPrivateCompanyInsights';
import { copy } from '../../../../../helpers/commonHelpers';
import {
  ONE_YEAR, FIVE_YEAR, ALL,
} from '../../../../../data/directory/topic_classes/ChartFilters';
import convertToDecimal from '../../../../../helpers/convertToDecimal';
import { checkAndRoun } from '../../../../../helpers/topicsHelpers';

import InsiderSvc from '../../../../../services/dbServices/InsiderSvc';

import Preloader from '../../../../Preloader';
import Plot from '../../../../Plot';
import ChartModal from './ChartModal';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const chartConfig = { responsive: true };

class ChartSection extends Component {
  constructor() {
    super();
    autoBind(this);

    this.state = {
      selectedPoint: {
        visible: false,
        date: null,
        x: 0,
        y: 0,
        showStories: true,
      },
      chartLayout: {
        height: 292,
        margin: {
          t: 10, b: 40, l: 1, r: 64,
        },
        ...defaultEquityChartLayoutData,
      },
      isChartLoading: true,
      isChartUpdating: true,
      chartGraph: defaultEquityChartBarData,
      chartGraphArr: [defaultEquityChartBarData],
      isChartHidden: false,
    };

    this.plot = createRef();
    this.chartModalRef = createRef();
    this.hoverEventHandled = false;
  }

  async componentDidMount() {
    this.cfMainWrapper = document.querySelector('.cf-main-wrapper');
    window.addEventListener('resize', this.onResize, { passive: true });
    this.onResize();
    this.callChartData();
  }

  componentDidUpdate(prevProps) {
    const { activeFilter, isChatbotOpened, activeType } = this.props;
    if (activeFilter !== prevProps.activeFilter || activeType !== prevProps.activeType) {
      this.callChartData(true);
    }
    if (isChatbotOpened !== prevProps.isChatbotOpened) {
      setTimeout(() => {
        this.onResize(isChatbotOpened);
      }, 700);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.onPageHover);
    window.removeEventListener('resize', this.onResize);
  }

  onPageHover(e) {
    if (!this.plot.current) return;

    const {
      current: {
        plot: {
          current: plot,
        },
      },
    } = this.plot;
    const { current: chartModalRef } = this.chartModalRef;

    if (
      !plot.contains(e.target) && chartModalRef && !chartModalRef.contains(e.target)
    ) {
      this.onModalClose();
    }
  }

  onChartHover(e, handleHoverEvent = true) {
    const date = e.points[0].x;
    const value = e.points[0].y;
    const { pointIndex } = e.points[0];
    const { pageX, pageY } = (
      (e.event.changedTouches && e.event.changedTouches[0])
      || e.event
    );

    let x;
    if (window.innerWidth >= 2800) {
      if (pageX + 300 <= window.innerWidth) {
        x = pageX - 300 > 18 ? pageX - 300 : 20;
      } else {
        x = window.innerWidth - 600;
      }
    } else if (window.innerWidth > 300) {
      if (pageX + 150 <= window.innerWidth) {
        x = pageX - 150 > 9 ? pageX - 150 : 10;
      } else {
        x = window.innerWidth - 300;
      }
    } else {
      x = 5;
    }

    const y = pageY + 25;

    this.setState((state) => ({
      selectedPoint: {
        ...state.selectedPoint,
        visible: true,
        date,
        x,
        y,
        value,
        pointIndex,
      },
    }));
    if (handleHoverEvent && !this.hoverEventHandled) {
      document.addEventListener('mousemove', this.onPageHover);
      this.hoverEventHandled = true;
    }
  }

  onModalClose() {
    this.setState(({ selectedPoint }) => ({
      selectedPoint: {
        ...selectedPoint,
        visible: false,
      },
    }));
    if (this.hoverEventHandled) {
      document.removeEventListener('mousemove', this.onPageHover);
      this.hoverEventHandled = false;
    }
  }

  onResize(bot) {
    const { width, isMainPage } = this.props;
    const { chartLayout } = this.state;
    const newObj = cloneDeep(chartLayout);

    if (width >= 2800) {
      newObj.height = isMainPage ? 664 : 584;
      newObj.margin = {
        t: 20, b: 80, l: 2, r: 128,
      };
      newObj.xaxis.linewidth = 2;
      newObj.yaxis.linewidth = 2;
      newObj.xaxis.tickfont.size = 24;
      newObj.yaxis.tickfont.size = 24;
    } else {
      if (width > 767 && width <= 1024) {
        newObj.height = isMainPage ? 332 : 306;
      } else {
        newObj.height = isMainPage ? 332 : 292;
      }
      if (width <= 767) {
        newObj.margin = {
          t: 10, b: 40, l: 1, r: 50,
        };
      } else {
        newObj.margin = {
          t: 10, b: 40, l: 1, r: 64,
        };
        if (bot) {
          newObj.margin = {
            t: 10, b: 40, l: 1, r: 63,
          };
        }
      }
      newObj.xaxis.linewidth = 1;
      newObj.yaxis.linewidth = 1;
      newObj.xaxis.tickfont.size = 12;
      newObj.yaxis.tickfont.size = 12;
    }

    this.setState({
      chartLayout: newObj,
    });
  }

  generateChartData(chartData = []) {
    const xData = [];
    const yData = [];
    const chartMainData = [];
    const { activeType } = this.props;
    const currentKey = activeType === 'count' ? 'transactionCount' : 'fundsRaised';
    for (let i = 0; i < chartData.length; i += 1) {
      const currentDate = chartData[i];
      xData.push(currentDate.date);
      yData.push(currentDate[currentKey]);
      chartMainData.push(currentDate);
    }

    return {
      xData, yData, chartMainData,
    };
  }

  yTicksFormat(data) {
    const newData = copy(data);
    const yValues = newData.sort((a, b) => a - b);
    let min = Number(yValues[0]);
    const max = Number(yValues[yValues.length - 1]);
    const step = (max - min) / 8;
    min += step;
    const avg = min !== max ? (max + min) / 2 : max;
    return [
      min,
      (avg + min) / 2,
      avg,
      (avg + max) / 2,
      max,
    ].map((value) => (
      convertToDecimal(value)
    ));
  }

  async callChartData(updateAPI) {
    const { activeFilter, extraParams } = this.props;
    if (updateAPI) {
      this.setState({
        isChartUpdating: true,
      });
    }
    const params = {
      time_period: activeFilter.toLowerCase(),
      ...extraParams,
    };
    if (activeFilter === ONE_YEAR || activeFilter === FIVE_YEAR || activeFilter === ALL) {
      params.aggregate_interval = 'monthly';
    }
    const chartAPIData = await InsiderSvc.getCompanyInsiderFundraisingGraphs(params);

    const { xData, yData, chartMainData } = this.generateChartData(chartAPIData);

    const markerColor = new Array(xData.length).fill('#88AADA');
    const chartGraph = {
      ...defaultEquityChartBarData,
      x: xData,
      y: yData,
      marker: {
        ...defaultEquityChartBarData.marker,
        color: markerColor,
      },
    };

    const { chartLayout } = this.state;
    const newObj = cloneDeep(chartLayout);
    newObj.xaxis.tickformat = activeFilter === ONE_YEAR
      || activeFilter === FIVE_YEAR || activeFilter === ALL ? '%b<br />%Y' : '%d %b<br />%Y';

    const customYTick = this.yTicksFormat(chartGraph.y);
    newObj.yaxis.tickvals = customYTick;
    newObj.yaxis.ticktext = customYTick.map((value) => checkAndRoun(value, 2, true, false));

    if (!xData.length) {
      this.setState({
        isChartHidden: true,
        isChartLoading: false,
        isChartUpdating: false,
        chartGraph,
        chartGraphArr: [chartGraph],
        chartMainData,
        chartLayout: newObj,
      });

      return;
    }

    this.setState({
      isChartLoading: false,
      isChartUpdating: false,
      isChartHidden: !xData.length,
      chartGraph,
      chartGraphArr: [chartGraph],
      chartMainData,
      chartLayout: newObj,
    });
  }

  render() {
    const {
      isChartHidden, isChartLoading, isChartUpdating,
      chartGraph, chartGraphArr, chartLayout, selectedPoint,
    } = this.state;
    const { activeFilter, activeType, natureOfBusiness } = this.props;

    const chartGraphObj = copy(chartGraph);
    if (selectedPoint?.visible) {
      chartGraphObj.marker.color[selectedPoint.pointIndex] = '#3971c1';
    }

    const chartEvents = {
      onHover: !isMobile ? this.onChartHover : null,
      onClick: this.onChartHover,
    };

    return (
      <div className={cx('charts-holder', { loading: isChartLoading })}>
        {isChartLoading ? (
          <Preloader
            loading
            top="unset"
          />
        ) : (
          <div className={cx('charts-holder-inner')}>
            {isChartUpdating && (
              <div className={cx('charts-holder-inner-loading')}>
                <Preloader
                  loading
                  static
                  top="unset"
                />
              </div>
            )}
            {isChartHidden ? (
              <div className={Styles['charts-holder-no-data']}>
                No data is available for this time period.
              </div>
            ) : (
              <>
                <Plot
                  data={[chartGraphObj]}
                  layout={chartLayout}
                  ref={this.plot}
                  config={chartConfig}
                  loaderClassname={cx('charts-holder-loader')}
                  {...chartEvents}
                />
              </>
            )}
          </div>
        )}

        {selectedPoint?.visible && (
          <ChartModal
            ref={this.chartModalRef}
            onClose={this.onModalClose}
            y={selectedPoint.y}
            x={selectedPoint.x}
            date={selectedPoint.date}
            currentValue={selectedPoint.value}
            activeFilter={activeFilter}
            activeType={activeType}
            natureOfBusiness={natureOfBusiness}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ common, chatbot }) => ({
  width: common.width,
  isChatbotOpened: chatbot.isChatbotOpened,
});

export default connect(mapStateToProps, null)(ChartSection);
